<template>
  <div class="inquiry-flow-app-inline inner inner-narrow">
    <!-- ステップ表示（ページ内展開） -->
    <div v-if="isStep" class="inquiry-flow-app-inline__steps">
      <!-- 上部メッセージ -->
      <p class="inquiry-flow-app-inline__header-message">
        <template v-if="isCurrentStep('category')">
          簡単1分、3つの質問に答えるだけ。<br />
          あなたの悩みと医師の回答が見つかる
        </template>
        <template v-else>
          {{ totalCount }}件の事例からあなたの悩みと医師の回答が見つかる
        </template>
      </p>
      <!-- Step 1: カテゴリ選択 -->
      <StepCategoryInline
        v-if="isCurrentStep('category')"
        v-model="formData.category"
        :keyword="keyword"
        @next="handleAnswer"
        @back="handleStepBack"
        @skip="handleSkip"
      />

      <!-- Step 2: 期間選択 -->
      <StepDurationInline
        v-if="isCurrentStep('duration')"
        v-model="formData.duration"
        :keyword="keyword"
        @next="handleAnswer"
        @back="handleStepBack"
        @skip="handleSkip"
      />

      <!-- Step 3: 受診状況選択 -->
      <StepMedicalVisitInline
        v-if="isCurrentStep('medicalVisit')"
        v-model="formData.medicalVisit"
        :keyword="keyword"
        @next="handleAnswer"
        @back="handleStepBack"
        @skip="handleSkip"
      />

      <!-- Step 4: 自由入力 -->
      <StepFreeTextInline
        v-if="isCurrentStep('freeText')"
        v-model="formData.freeText"
        v-model:age="formData.targetAge"
        v-model:gender="formData.targetGender"
        :keyword="keyword"
        @search="handleSearch"
        @back="handleStepBack"
      />
    </div>

    <!-- 検索結果ダイアログ -->
    <dialog
      v-if="hasSearched"
      ref="resultDialogRef"
      class="inquiry-flow-result-dialog"
      @cancel.prevent="handleClose"
      @click="handleDialogClick"
    >
      <div class="inquiry-flow-result-dialog__container">
        <!-- ローディング -->
        <LoadingBar v-if="isLoading" />
        <!-- 検索結果 -->
        <SearchResults
          v-else
          v-model="searchConditionData"
          :results="results"
          :has-error="hasError"
          :keyword="keyword"
          :is-loading="isLoading"
          ga-category="問診LP_inline"
          @close="handleClose"
          @retry="handleRetry"
          @re-search="handleReSearch"
        />
      </div>
    </dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, nextTick } from 'vue';
import StepCategoryInline from './StepCategoryInline.vue';
import StepDurationInline from './StepDurationInline.vue';
import StepMedicalVisitInline from './StepMedicalVisitInline.vue';
import StepFreeTextInline from './StepFreeTextInline.vue';
import LoadingBar from '../../../similar_search/components/LoadingBar.vue';
import SearchResults from '../SearchResults.vue';
import { searchSimilarTopics } from '../../../similar_search/api';
import { buildSearchQuery } from '../../buildSearchQuery';
import type {
  InquiryStep,
  InquiryFormData,
  TargetAge,
  TargetGender,
  SimilarSearchResult,
} from '../../types';

const props = defineProps<{
  keyword: string;
  targetAge: TargetAge;
  targetGender: TargetGender;
  totalCount: number;
}>();

// DOM refs
const resultDialogRef = ref<HTMLDialogElement | null>(null);

// ステップ管理
const currentStepIndex = ref(0);

// 非同期状態管理
const isLoading = ref(false);
const hasSearched = ref(false);
const hasError = ref(false);

// フォームデータ
const formData = reactive<InquiryFormData>({
  keyword: props.keyword,
  targetAge: props.targetAge,
  targetGender: props.targetGender,
  category: null,
  duration: null,
  medicalVisit: null,
  freeText: '',
});

// 検索結果
const results = ref<SimilarSearchResult[]>([]);

// ステップの順序定義
const stepOrder: InquiryStep[] = [
  'category',
  'duration',
  'medicalVisit',
  'freeText',
];

// 派生状態
const isStep = computed(() => !hasSearched.value && !isLoading.value);

// ステップ判定ヘルパー
const isCurrentStep = (step: InquiryStep) => {
  return stepOrder[currentStepIndex.value] === step;
};

// フォームデータを別のオブジェクトにコピーする
const copyFormData = ({
  target,
  source,
}: {
  target: InquiryFormData;
  source: InquiryFormData;
}): void => {
  target.keyword = source.keyword;
  target.targetAge = source.targetAge;
  target.targetGender = source.targetGender;
  target.category = source.category;
  target.duration = source.duration;
  target.medicalVisit = source.medicalVisit;
  target.freeText = source.freeText;
};

// 検索条件データ（SearchConditionAccordion専用）
const searchConditionData = ref<InquiryFormData>({
  keyword: props.keyword,
  targetAge: props.targetAge,
  targetGender: props.targetGender,
  category: null,
  duration: null,
  medicalVisit: null,
  freeText: '',
});

// 回答時の処理（GA送信 + 次のステップへ）
const handleAnswer = () => {
  const currentStepName = stepOrder[currentStepIndex.value];

  // GA送信
  let answerValue = '';
  if (currentStepName === 'category' && formData.category) {
    answerValue = formData.category;
  } else if (currentStepName === 'duration' && formData.duration) {
    answerValue = formData.duration;
  } else if (currentStepName === 'medicalVisit' && formData.medicalVisit) {
    answerValue = formData.medicalVisit;
  }

  window.sendGAEvent(
    '問診LP_inline',
    'answer',
    `step_${currentStepName}_${answerValue}`,
  );

  // 次のステップへ
  forwardNextStep();
};

// 次のステップへ進める（内部処理）
const forwardNextStep = () => {
  if (currentStepIndex.value < stepOrder.length - 1) {
    currentStepIndex.value++;
  }
};

// 前のステップへ
const handleStepBack = () => {
  if (currentStepIndex.value > 0) {
    currentStepIndex.value--;
  }
};

// スキップ
const handleSkip = () => {
  const currentStepName = stepOrder[currentStepIndex.value];
  window.sendGAEvent('問診LP_inline', 'skip', `step_${currentStepName}`);
  forwardNextStep();
};

// 検索実行
const handleSearch = async () => {
  window.sendGAEvent(
    '問診LP_inline',
    'search',
    isCurrentStep('freeText') && formData.freeText
      ? `freeText_length_${formData.freeText.length}`
      : 'from_steps',
  );

  // 常にformDataをsearchConditionDataにコピー
  copyFormData({ target: searchConditionData.value, source: formData });

  // ダイアログを先に表示してからローディング開始
  hasSearched.value = true;
  hasError.value = false;
  isLoading.value = true;

  // ダイアログを表示
  await nextTick();
  resultDialogRef.value?.showModal();

  try {
    const query = buildSearchQuery(searchConditionData.value);
    const response = await searchSimilarTopics({ query });
    results.value = response.results;

    window.sendGAEvent(
      '問診LP_inline',
      'result',
      `${response.results.length}件`,
    );
  } catch (error) {
    console.error('検索エラー:', error);
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
};

// SearchConditionAccordionからの再検索
const handleReSearch = async () => {
  // searchConditionDataをそのまま使用（コピーしない）
  hasError.value = false;
  isLoading.value = true;

  try {
    const query = buildSearchQuery(searchConditionData.value);
    const response = await searchSimilarTopics({ query });
    results.value = response.results;

    window.sendGAEvent(
      '問診LP_inline',
      're-search',
      `${response.results.length}件`,
    );
  } catch (error) {
    console.error('検索エラー:', error);
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
};

// リトライ
const handleRetry = () => {
  hasSearched.value = false;
  hasError.value = false;
  results.value = [];
  currentStepIndex.value = 0;
  resultDialogRef.value?.close();
};

// ダイアログを閉じる
const handleClose = () => {
  resultDialogRef.value?.close();
  hasSearched.value = false;
  hasError.value = false;
  results.value = [];
  currentStepIndex.value = 0;
};

// ダイアログのクリックイベント
const handleDialogClick = (e: MouseEvent) => {
  const dialogElement = resultDialogRef.value;
  if (!dialogElement) return;

  if (e.target === dialogElement) {
    handleClose();
  }
};
</script>

<style lang="scss" scoped>
@import '~green/_variables.scss';

.inquiry-flow-app-inline {
  width: 100%;
  max-width: 600px;
  background-color: $primary-background-color;
  padding: 24px;

  &__header-message {
    text-align: center;
    font-size: 1rem;
    font-weight: bold;
    color: #0ba780;
    margin: 0;
    padding: 12px 16px 0 16px;
    line-height: 1.5;
  }
}

// 結果ダイアログ
.inquiry-flow-result-dialog {
  padding: 0;
  border: none;
  border-radius: 12px;
  max-width: 600px;
  width: calc(100% - 32px);
  max-height: calc(100vh - 48px);
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  margin: auto;

  &::backdrop {
    background: rgba(0, 0, 0, 0.6);
  }

  &__container {
    display: flex;
    flex-direction: column;
    max-height: calc(100vh - 48px);
    overflow-y: auto;
    background: #fcfaf6;
  }
}

@media (max-width: 640px) {
  .inquiry-flow-result-dialog {
    width: 100%;
    max-width: 100%;
    max-height: 100%;
    height: 100%;
    border-radius: 0;

    &__container {
      max-height: 100%;
      height: 100%;
    }
  }
}
</style>
